<template>
  <div id="app">
    <SemcogHeader>
      <span v-on:click="inerMode()"
       style="line-height: 2em; font-size: 1em;"
      >
        Community Explorer
      </span>
    </SemcogHeader>
    <Loader v-if="downloadingData > 0"/>
    <MainMap
      v-bind:geotype="geotype"
      v-bind:allGeojsonByType="allGeojsonByType"
      v-bind:mappedIndicator="mappedIndicator"
      v-bind:normalizationIndicator="normalizationIndicator"
      v-bind:indFromName="indFromName"
      v-bind:getIndMoeName="getIndMoeName"
      v-bind:groupNames="groupNames"
      v-bind:groups="groups"
      v-bind:selectedShortcut="selectedShortcut"
      v-bind:namesFromGeotype="namesFromGeotype"
      v-bind:geotypesForMap="geotypesForMap"
      v-bind:selectedId="selectedId"
      v-bind:hoverId="printing ? null: hoverId"
      v-bind:filteredCounty="filteredCounty"
      v-bind:inInerMode="inInerMode"
      v-on:set-sel-geom-id="setSelected($event)"
      v-on:set-hov-geom-id="hoverId = $event"
      v-on:set-ind="mappedIndicator = $event"
      v-on:set-norm="normalizationIndicator = $event"
      v-on:set-map="setMap($event)"
      v-on:set-geo-type="setGeoType($event)"
      v-on:set-legend-color-data="setLegendData($event)"
    />
    <Profile
      v-bind:shortcuts="availableShortcuts"
      v-bind:groupNames="groupNames"
      v-bind:groups="groups"
      v-bind:groupDesc="groupDesc"
      v-bind:getIndName="getIndName"
      v-bind:getIndYear="getIndYear"
      v-bind:getIndMoeName="getIndMoeName"
      v-bind:incomeChartIndicators="incomeChartIndicators"
      v-bind:incomeChartCutoffs="incomeChartCutoffs"
      v-bind:ageDataMaleChartIndicators="ageDataMaleChartIndicators"
      v-bind:ageDataFemaleChartIndicators="ageDataFemaleChartIndicators"
      v-bind:allGeogData="filteredGeogData"
      v-bind:countyData="countyData"
      v-bind:regionData="regionData"
      v-bind:selectedId="selectedId"
      v-bind:filteredCounty="filteredCounty"
      v-bind:selectedName="selectedName"
      v-bind:namesForSelectedType="namesForSelectedType"
      v-bind:namesForCountyFilter="namesForCountyFilter"
      v-bind:hoverId="printing ? null: hoverId"
      v-bind:geotype="geotype"
      v-bind:namesFromGeotype="namesFromGeotype"
      v-bind:selectedShortcut="selectedShortcut"
      v-bind:colorLegendData="printing ? null: colorLegendData"
      v-bind:inInerMode="inInerMode"
      v-bind:printing="printing"
      v-on:set-sel-geom-id="setSelected($event)"
      v-on:set-hov-geom-id="hoverId = $event"
      v-on:set-map="setMap($event)"
      v-on:set-filtered-county="setFilteredCounty($event)"
    />
  </div>
</template>

<script>
import debounce from 'lodash.debounce'
import { diff } from 'deep-diff';
import * as turf from 'turf'

import Profile from './components/Profile.vue'
import MainMap from './components/MainMap.vue'
import SemcogHeader from './components/SemcogHeader.vue'
// eslint-disable-next-line
const names_csv = require('./names.csv')
// eslint-disable-next-line
const shortcuts_csv = require('./shortcuts.csv')

import { featureCollection } from 'turf'
import Loader from './components/map/Loader'

export default {
  name: 'app',
  components: { Loader, MainMap, Profile, SemcogHeader },
  data: function () {
    let shortcuts = Object.freeze(shortcuts_csv);
    let shortcut = shortcuts.find(s => s.url === this.$route.query.shortcut) || {
      numerator: 'totalpop_24',
      denominator: 'constant',
    };
    if (shortcut.url) { // the url has a shortcut that we interpreted correctly
      // eslint-disable-next-line no-undef
      ga('send', 'event', 'url-selectedShortcut', shortcut.url, 'Community Explorer')
      // eslint-disable-next-line no-undef
      gtag('event', "url-selectedShortcut", {'change': shortcut.url, 'map_name': 'Community Explorer'});
    }
    let filteredCounty = parseInt(this.$route.query.filtered) || null;
    if (filteredCounty) {
      // eslint-disable-next-line no-undef
      ga('send', 'event', 'url-filteredCounty', filteredCounty, 'Community Explorer')
      // eslint-disable-next-line no-undef
      gtag('event', "url-filteredCounty", {'change': filteredCounty, 'map_name': 'Community Explorer'});
    }
    let selectedId = parseInt(this.$route.query.community) || null;
    if (selectedId) {
      // eslint-disable-next-line no-undef
      ga('send', 'event', 'url-selectedId', selectedId, 'Community Explorer')
      // eslint-disable-next-line no-undef
      gtag('event', "url-selectedId", {'change': selectedId, 'map_name': 'Community Explorer'});
    }
    return {
      downloadingData: 0,
      printing: false,
      inInerMode: false,
      selectedId,
      hoverId: null,
      filteredCounty,
      geotype: 'mcd',
      geotypes: Object.freeze(['region', 'county', 'mcd', 'SchoolDistrict', 'CensusTract']), // , 'BlockGroup'
      geotypesForMap: Object.freeze(['county', 'mcd', 'SchoolDistrict', 'CensusTract']),
      namesFromGeotype: Object.freeze({
        'county': {
          name: 'Counties',
          singularName: 'County',
        },
        'SchoolDistrict': {
          name: 'School Districts',
          singularName: 'School District',
        },
        'mcd': {
          name: 'Communities',
          singularName: 'Community',
        },
        'CensusTract': {
          name: 'Census Tracts',
          singularName: 'Census Tract',
        },
      }),
      mappedIndicator: shortcut.numerator,
      normalizationIndicator: shortcut.denominator,
      groupDesc: Object.freeze({
        'Population': {
          desc:
            'Southeast Michigan is home to a diverse population of over <strong>4.8 million</strong> people that make up our cities, villages, and townships.',
          descExtra: 'All people, male and female, child and adult, living in a given geographic area.  Source: American Community Survey 2020-2024 5-Year Estimates.',
          iconName: 'user-alt',
        },
        'Households': {
          desc:
            'There are over <strong>1.9 million</strong> households in Southeast Michigan, averaging <strong>2.5</strong> people per household.',
          descExtra: 'A household includes all the people who occupy a housing unit as their usual place of residence. The residents of an occupied housing unit are collectively referred to as a household.  Source: American Community Survey 2020-2024 5-Year Estimates.',
          iconName: 'users',
        },
        'Internet': {
          longName: 'Internet/Computer Access',
          desc: 'Access to internet and computing devices is more critical than ever. Over <strong>90%</strong> of our region\'s households have access to these important resources.',
          descExtra: 'Source: American Community Survey 2020-2024 5-Year Estimates.', // TODO
          iconName: 'wifi',
        },
        'Housing': {
          desc:
            'Just over <strong>2 million</strong> housing units provide residential choices in Southeast Michigan.',
          descExtra: 'Housing unit is an independent house, a townhome, an apartment, or a mobile home or trailer that is either occupied or vacant. Source: American Community Survey 2020-2024 5-Year Estimates.',
          iconName: 'home',
        },
        'Economics': {
          desc:
            'Economic activities support more than <strong>2.9 million</strong> jobs in Southeast Michigan.',
          descExtra: 'Jobs and job densities are from 2019 SEMCOG estimates and represent total full-time and part-time employment including both wage and salary jobs, as well as self-employed jobs. Other economic indicators are based on American Community Survey 2020-2024 5-Year Estimates.',
          iconName: 'dollar-sign',
        },
      }),
      shortcuts,
      incomeChartIndicators: Object.freeze([
        'ehhlessthan_10000_24',
        'ehh_10000to_14999_24',
        'ehh_15000to_19999_24',
        'ehh_20000to_24999_24',
        'ehh_25000to_29999_24',
        'ehh_30000to_34999_24',
        'ehh_35000to_39999_24',
        'ehh_40000to_44999_24',
        'ehh_45000to_49999_24',
        'ehh_50000to_59999_24',
        'ehh_60000to_74999_24',
        'ehh_75000to_99999_24',
        'ehh_100000to_124999_24',
        'ehh_125000to_149999_24',
        'ehh_150000to_199999_24',
        'ehh_200000ormore_24',
      ]),
      incomeChartCutoffs: Object.freeze([
        0,
        10000,
        15000,
        20000,
        25000,
        30000,
        35000,
        40000,
        45000,
        50000,
        60000,
        75000,
        100000,
        125000,
        150000,
        200000,
        250000,
      ]),
      ageDataMaleChartIndicators: Object.freeze([
        'popmale0_4_24',
        'popmale5_9_24',
        'popmale10_14_24',
        'popmale15_19_24',
        'popmale20_24_24',
        'popmale25_29_24',
        'popmale30_34_24',
        'popmale35_39_24',
        'popmale40_44_24',
        'popmale45_49_24',
        'popmale50_54_24',
        'popmale55_59_24',
        'popmale60_64_24',
        'popmale65_69_24',
        'popmale70_74_24',
        'popmale75_79_24',
        'popmale80_84_24',
        'popmale85andup_24',
      ]),
      ageDataFemaleChartIndicators: Object.freeze([
        'popfemale0_4_24',
        'popfemale5_9_24',
        'popfemale10_14_24',
        'popfemale15_19_24',
        'popfemale20_24_24',
        'popfemale25_29_24',
        'popfemale30_34_24',
        'popfemale35_39_24',
        'popfemale40_44_24',
        'popfemale45_49_24',
        'popfemale50_54_24',
        'popfemale55_59_24',
        'popfemale60_64_24',
        'popfemale65_69_24',
        'popfemale70_74_24',
        'popfemale75_79_24',
        'popfemale80_84_24',
        'popfemale85andup_24',
      ]),
      colorLegendData: null,
      allGeojsonByType: Object.freeze(new Map()),
    }
  },
  mounted: function () {
    const mql = window.matchMedia('print')
    const cb = ({ matches }) => {
      this.printing = matches
    }
    // IOS dose not  work with addEventListener
    mql.addListener(cb)
    cb(mql)
  },
  computed: {
    geojsonFile: function () {
      if (this.inInerMode) {
        return './geojson/full_boundaries.geojson'
      }
      return './geojson/boundaries.geojson'
    },
    nameFromId: function () {
      let nameFromId = new Map()
      this.geotypes.forEach(type => {
        nameFromId.set(type, new Map((this.allGeogData.get(type) || []).map(val => [val.geoid, [(val.name || '').trim(), val.county]])))
      })
      return nameFromId
    },
    namesForSelectedType: function () {
      if (!this.nameFromId.get(this.geotype)) {
        return new Map()
      }
      if (!this.filteredCounty) {
        return this.nameFromId.get(this.geotype)
      }
      return new Map(Array.from(this.nameFromId.get(this.geotype)).filter(g => g[1][1] === this.filteredCounty))
    },
    namesForCountyFilter: function () {
      return this.nameFromId.get('county') || new Map()
    },
    filteredGeogData: function () {
      if (!this.allGeogData.get(this.geotype)) {
        return []
      }
      if (!this.filteredCounty) {
        return this.allGeogData.get(this.geotype)
      }
      return this.allGeogData.get(this.geotype).filter(g => (g.county === this.filteredCounty) || (g.county2 === this.filteredCounty))
    },
    selectedName: function () {
      let out = this.namesForSelectedType.get(this.selectedId)
      if (!out) {
        return
      }
      return out[0]
    },
    selectedCounty: function () {
      let out = this.namesForSelectedType.get(this.hoverId || this.selectedId)
      if (!out) {
        return this.filteredCounty
      }
      return out[1]
    },
    countyData: function () {
      let out = this.allGeogData.get('county')
      if (!out) {
        return {}
      }
      return out.find(row => row.geoid === this.selectedCounty) || {}
    },
    regionData: function () {
      let out = this.allGeogData.get('region')
      if (!out) {
        return {}
      }
      return out.find(r => r.geoid === 8999)
    },
    indFromName: function () {
      let out = {}
      let columnsInGeojson = this.columnsByGeoType.get(this.geotype)
      if (!columnsInGeojson || !columnsInGeojson.values) {
        return out
      }
      for (let ind of columnsInGeojson.values()) {
        if (ind.startsWith('m_')) {
          continue // all moe's start with 'm_'
        }
        let name = this.getIndName(ind)
        if (!out[name]) {
          out[name] = {}
        }
        out[name][this.getIndYear(ind)] = ind
      }
      return out
    },
    availableShortcuts () {
      let columnsInGeojson = this.columnsByGeoType.get(this.geotype)
      if (!columnsInGeojson || !columnsInGeojson.has) {
        return []
      }
      return this.shortcuts.filter(
        s => (columnsInGeojson.has(s.numerator)) && (columnsInGeojson.has(s.denominator))
      )
    },
    selectedShortcut: function () {
      return this.availableShortcuts.find(s => s.numerator === this.mappedIndicator && s.denominator === this.normalizationIndicator)
    },
    groupNames: function () {
      let groups = new Set()
      for (let short of this.availableShortcuts) {
        groups.add(short.group)
      }
      return Array.from(groups)
    },
    groups: function () {
      let groups = {}
      for (let short of this.availableShortcuts) {
        if (!groups[short.group]) {
          groups[short.group] = []
        }
        groups[short.group].push(short)
      }
      return groups
    },
    namesFromCore: function () {
      let out = {}
      names_csv.forEach(f => {
        out[f.core] = f.name
      })
      return out
    },
    columnsByGeoType: function () {
      let columnsByGeoType = new Map()
      this.geotypes.forEach(type => {
        let g = new Set();
        (this.allGeogData.get(type) || []).forEach(f =>
          Object.keys(f).forEach(c => {
              if (f[c] && !isNaN(f[c])) {
                g.add(c)
              }
            },
          ),
        )
        columnsByGeoType.set(type, g)
      })
      return columnsByGeoType
    },
    allGeogData: function () {
      let allGeogData = new Map()
      this.geotypes.forEach(type => {
        let json = this.allGeojsonByType.get(type)
        if (json && json.features) {
          allGeogData.set(type, json.features.map(f => f.properties))
        }
      })
      return allGeogData
    },
    urlData: function () {
      if (this.downloadingData) {
        // none of our computed are right without data so just leave the url the same
        return this.$route.query
      }
      let query = {};
      if (this.geotype === 'mcd' && this.selectedId && this.selectedId !== 8999) {
        query.community = this.selectedId.toString()
      }
      if (this.filteredCounty) {
        query.filtered = this.filteredCounty.toString()
      }
      if (this.selectedShortcut && this.selectedShortcut.url) {
        query.shortcut = this.selectedShortcut.url.toString()
      }
      return query;
    },
  },
  methods: {
    inerMode: function () {
      if (process.env.NODE_ENV === 'development' || window.location.hostname === 'storydev') {
        this.inInerMode = true
      }
    },
    getIndName: function (ind) {
      let core = ind
      if (ind.match(/_\d\d$/gi)) {
        core = ind.slice(0, -3)
      }
      return this.namesFromCore[core] || core
    },
    getIndMoeName: function (ind) {
      let columnsInGeojson = this.columnsByGeoType.get(this.geotype)
      let moe = undefined
      if (!columnsInGeojson || !columnsInGeojson.has) {
        return moe
      }
      if (columnsInGeojson.has('m_' + ind)) {
        moe = 'm_' + ind
      }
      return moe
    },
    getIndYear: function (ind) {
      let year = ind.match(/_\d\d$/gi)
      if (year) {
        year = 2000 + parseInt(year[0].slice(1), 10)
      }
      return year || 'N/A'
    },
    setFilteredCounty (id) {
      this.filteredCounty = +id || id
      if (!this.namesForSelectedType.get(this.selectedId)) {
        this.selectedId = null
      }
    },
    setSelected (id) {
      this.selectedId = id
      if (!id || id === 8999) {
        this.setFilteredCounty(null)
      }
    },
    setMap (id) {
      this.mappedIndicator = id[0]
      this.normalizationIndicator = id[1]
    },
    setLegendData: debounce(function (id) {
      this.colorLegendData = Object.freeze(id)
    }, 200),
    setGeoType: function (id) {
      this.geotype = id
      this.hoverId = null
      this.selectedId = null

      let columnsInGeojson = this.columnsByGeoType.get(id)

      if (!columnsInGeojson.has(this.mappedIndicator) || !columnsInGeojson.has(this.normalizationIndicator)) {
        this.mappedIndicator = 'totalpop_24'
        this.normalizationIndicator = 'acres'
      }

      if (id === 'county') {
        this.filteredCounty = null
      }
    },
  },
  watch: {
    geojsonFile: {
      immediate: true,
      handler:
        function () {
          this.downloadingData += 1
          fetch(this.geojsonFile).then(r => r.json()).then(data => {
            if (data.description) {
              let names = data.description.split(',')
              data.features.forEach(f => {
                let out = {}
                Object.keys(f.properties).forEach(p => {
                  out[names[parseInt(p, 16)]] = f.properties[p]
                })
                f.properties = out
              })
            }
            let offset = data.features.find(f => f.properties.geoid === 8999)
            if (offset) {
              offset = offset.geometry && offset.geometry.coordinates
              if (offset.length && offset.length === 2) {
                let offset_x = offset[0]
                let offset_y = offset[1]
                turf.coordEach(data, pointCoords => {
                  pointCoords[0] -= offset_x
                  pointCoords[1] -= offset_y
                })
              }
            }
            const geotypes = new Set(data.features.map(row => row.properties.geotype))
            let allGeojsonByType = new Map()
            geotypes.forEach(type => {
              allGeojsonByType.set(type, featureCollection(data.features.filter(row => row.properties.geotype === type)))
            })
            this.allGeojsonByType = Object.freeze(allGeojsonByType)
            this.$nextTick(() => {
              this.downloadingData -= 1
            })
          });
        },
    },
    urlData: function (newUrl, oldUrl) {
      if (diff(oldUrl, newUrl)) {
         this.$router.replace({ query: this.urlData })
      }
    },
    geotype: function (n, o) {
      if (n && diff(n, o)) {
        // eslint-disable-next-line no-undef
        ga('send', 'event', 'change-geotype', n, 'Community Explorer')
        // eslint-disable-next-line no-undef
        gtag('event', "change-geotype", {'change': n, 'map_name': 'Community Explorer'});
      }
    },
    filteredCounty: function (n, o) {
      if (n && diff(n, o)) {
        // eslint-disable-next-line no-undef
        ga('send', 'event', 'change-filteredCounty', n, 'Community Explorer')
        // eslint-disable-next-line no-undef
        gtag('event', "change-filteredCounty", {'change': n, 'map_name': 'Community Explorer'});
      }
    },
    selectedShortcut: function (n, o) {
      if (n && n.url && o &&  diff(o, n)) {
        // eslint-disable-next-line no-undef
        ga('send', 'event', 'change-selectedShortcut', n.url, 'Community Explorer')
        // eslint-disable-next-line no-undef
        gtag('event', "change-selectedShortcut", {'change': n.url, 'map_name': 'Community Explorer'});
      }
    },
    selectedId: function (n, o) {
      if (n && diff(n, o) && n !== 8999) {
        // eslint-disable-next-line no-undef
        ga('send', 'event', 'change-selectedId', n, 'Community Explorer')
        // eslint-disable-next-line no-undef
        gtag('event', "change-selectedId", {'change': n, 'map_name': 'Community Explorer'});
      }
    },
  },
}

</script>

<style scoped>
  #app {
    width: 100%;
    height: 100vh;
    display: grid;
    grid-template-columns: 35% auto;
    grid-template-rows: max-content auto;
    font-family: Arial, Helvetica, sans-serif;
  }

  @media (max-width: 576px), print {
    #app {
      height: initial;
      grid-template-columns: auto;
      grid-template-rows: max-content max-content auto;
    }
  }
</style>
